import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  ClockIcon,
  FireIcon,
  BoltIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  CalculatorIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

// Mock data para estadísticas de mercado
const marketStats = {
  totalVolume: 125000000,
  totalVolumeChange: 12.5,
  dailyVolume: 2500000,
  dailyVolumeChange: -3.2,
  totalAuctions: 45678,
  activeAuctions: 234,
  avgPrice: 8.5,
  avgPriceChange: 5.7,
};

const topCollections = [
  {
    rank: 1,
    name: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 1.png',
    floorPrice: 68.5,
    change24h: 5.2,
    volume7d: 12500,
    sales7d: 156,
  },
  {
    rank: 2,
    name: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology1.jpg',
    floorPrice: 42.3,
    change24h: -2.1,
    volume7d: 8900,
    sales7d: 234,
  },
  {
    rank: 3,
    name: 'Equus Dualis II — Rare',
    image: '/Equus Dualis/Equus Dualis II ) 15.png',
    floorPrice: 12.8,
    change24h: 8.5,
    volume7d: 5600,
    sales7d: 412,
  },
  {
    rank: 4,
    name: 'Warrior Mythology — Origins',
    image: '/warrior mythology/warrior mythology20.jpg',
    floorPrice: 6.2,
    change24h: 1.3,
    volume7d: 3200,
    sales7d: 567,
  },
  {
    rank: 5,
    name: 'Equus Dualis II — Legends',
    image: '/Equus Dualis/Equus Dualis II ) 28.png',
    floorPrice: 4.8,
    change24h: -4.7,
    volume7d: 2100,
    sales7d: 389,
  },
];

const platformFees = [
  {
    tier: 'Sin Staking',
    color: 'bg-gray-500',
    sellerFee: 2.5,
    buyerFee: 0,
    features: ['Acceso a todas las subastas', 'Historial de transacciones', 'Soporte básico'],
  },
  {
    tier: 'Bronze',
    stakeRequired: '1,000 NDH',
    color: 'bg-gradient-to-r from-amber-700 to-amber-600',
    sellerFee: 2.25,
    buyerFee: 0,
    discount: 10,
    features: ['10% descuento en fees', 'Badge de perfil', 'Acceso anticipado a nuevas funciones'],
  },
  {
    tier: 'Silver',
    stakeRequired: '10,000 NDH',
    color: 'bg-gradient-to-r from-slate-400 to-slate-300',
    sellerFee: 2.0,
    buyerFee: 0,
    discount: 20,
    features: ['20% descuento en fees', 'Soporte prioritario', 'Subastas exclusivas mensuales'],
  },
  {
    tier: 'Gold',
    stakeRequired: '50,000 NDH',
    color: 'bg-gradient-to-r from-yellow-400 to-amber-500',
    sellerFee: 1.75,
    buyerFee: 0,
    discount: 30,
    features: ['30% descuento en fees', 'Acceso a subastas VIP', 'Curación prioritaria', 'Gobernanza DAO'],
  },
  {
    tier: 'Platinum',
    stakeRequired: '100,000 NDH',
    color: 'bg-gradient-to-r from-slate-300 to-indigo-400',
    sellerFee: 1.625,
    buyerFee: 0,
    discount: 35,
    features: ['35% descuento en fees', 'Todas las funciones Gold', 'Línea directa de soporte', 'Eventos exclusivos'],
  },
];

const recentSales = [
  { name: 'Equus Dualis II #4',   price: 125.5, currency: 'ETH', time: 'hace 2 min' },
  { name: 'Warrior Mythology #31', price: 67.2,  currency: 'ETH', time: 'hace 5 min' },
  { name: 'Equus Dualis II #19',  price: 18.9,  currency: 'ETH', time: 'hace 12 min' },
  { name: 'Warrior Mythology #8',  price: 12.3,  currency: 'ETH', time: 'hace 18 min' },
  { name: 'Equus Dualis II #26',  price: 8.7,   currency: 'ETH', time: 'hace 25 min' },
];

const MarketPricingPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [calculatorPrice, setCalculatorPrice] = useState(10);
  const [calculatorTier, setCalculatorTier] = useState('Sin Staking');

  const calculateFee = (price: number, tier: string) => {
    const tierData = platformFees.find(t => t.tier === tier);
    if (!tierData) return { fee: 0, net: 0 };
    const fee = price * (tierData.sellerFee / 100);
    const net = price - fee;
    return { fee: fee.toFixed(4), net: net.toFixed(4) };
  };

  const feeResult = calculateFee(calculatorPrice, calculatorTier);

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <ChartBarIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Precios y <span className="gradient-text">Mercado</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Estadísticas de mercado, floor prices y estructura de comisiones
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-8 border-b border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <CurrencyDollarIcon className="w-5 h-5" />
                <span className="text-sm">Volumen Total</span>
              </div>
              <p className="text-2xl font-bold text-white">
                ${(marketStats.totalVolume / 1000000).toFixed(1)}M
              </p>
              <p className={`text-sm flex items-center gap-1 ${
                marketStats.totalVolumeChange >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {marketStats.totalVolumeChange >= 0 ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4" />
                )}
                {marketStats.totalVolumeChange >= 0 ? '+' : ''}{marketStats.totalVolumeChange}%
              </p>
            </div>

            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <ClockIcon className="w-5 h-5" />
                <span className="text-sm">Volumen 24h</span>
              </div>
              <p className="text-2xl font-bold text-white">
                ${(marketStats.dailyVolume / 1000000).toFixed(2)}M
              </p>
              <p className={`text-sm flex items-center gap-1 ${
                marketStats.dailyVolumeChange >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {marketStats.dailyVolumeChange >= 0 ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4" />
                )}
                {marketStats.dailyVolumeChange >= 0 ? '+' : ''}{marketStats.dailyVolumeChange}%
              </p>
            </div>

            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <FireIcon className="w-5 h-5" />
                <span className="text-sm">Subastas Activas</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {marketStats.activeAuctions}
              </p>
              <p className="text-sm text-gray-500">
                de {marketStats.totalAuctions.toLocaleString()} totales
              </p>
            </div>

            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <BoltIcon className="w-5 h-5" />
                <span className="text-sm">Precio Promedio</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {marketStats.avgPrice} ETH
              </p>
              <p className={`text-sm flex items-center gap-1 ${
                marketStats.avgPriceChange >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {marketStats.avgPriceChange >= 0 ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4" />
                )}
                {marketStats.avgPriceChange >= 0 ? '+' : ''}{marketStats.avgPriceChange}%
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Collections & Recent Sales */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {/* Top Collections */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Top Colecciones</h2>
                <div className="flex gap-2">
                  {['24h', '7d', '30d'].map((period) => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        selectedPeriod === period
                          ? 'bg-primary-500 text-white'
                          : 'bg-dark-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-700">
                      <th className="text-left px-6 py-4 text-gray-400 text-sm font-medium">#</th>
                      <th className="text-left px-6 py-4 text-gray-400 text-sm font-medium">Colección</th>
                      <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium">Floor Price</th>
                      <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium">24h %</th>
                      <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium hidden md:table-cell">Volumen {selectedPeriod}</th>
                      <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium hidden md:table-cell">Ventas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCollections.map((collection) => (
                      <tr key={collection.rank} className="border-b border-dark-700 last:border-0 hover:bg-dark-700/30">
                        <td className="px-6 py-4 text-gray-400">{collection.rank}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={collection.image}
                              alt={collection.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <span className="text-white font-medium">{collection.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-white font-medium">
                          {collection.floorPrice} ETH
                        </td>
                        <td className={`px-6 py-4 text-right ${
                          collection.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {collection.change24h >= 0 ? '+' : ''}{collection.change24h}%
                        </td>
                        <td className="px-6 py-4 text-right text-gray-300 hidden md:table-cell">
                          {collection.volume7d.toLocaleString()} ETH
                        </td>
                        <td className="px-6 py-4 text-right text-gray-300 hidden md:table-cell">
                          {collection.sales7d}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Sales */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Ventas Recientes</h2>
              <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-4">
                <div className="space-y-4">
                  {recentSales.map((sale, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center justify-between py-2 border-b border-dark-700 last:border-0"
                    >
                      <div>
                        <p className="text-white text-sm font-medium">{sale.name}</p>
                        <p className="text-gray-500 text-xs">{sale.time}</p>
                      </div>
                      <p className="text-primary-400 font-bold">
                        {sale.price} {sale.currency}
                      </p>
                    </motion.div>
                  ))}
                </div>
                <Link
                  to="/live"
                  className="block mt-4 text-center text-primary-400 hover:text-primary-300 text-sm"
                >
                  Ver todas las ventas →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Fees */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Estructura de Comisiones</h2>
              <p className="text-gray-400">
                Comisiones transparentes con descuentos por stakear NDH
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {platformFees.map((tier, idx) => (
                <motion.div
                  key={tier.tier}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
                >
                  <div className={`p-4 ${tier.color}`}>
                    <h3 className="text-lg font-bold text-white">{tier.tier}</h3>
                    {tier.stakeRequired && (
                      <p className="text-white/80 text-sm">{tier.stakeRequired}</p>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="mb-4">
                      <p className="text-gray-400 text-xs mb-1">Fee Vendedor</p>
                      <p className="text-2xl font-bold text-white">{tier.sellerFee}%</p>
                      {tier.discount && (
                        <p className="text-green-400 text-xs">-{tier.discount}% descuento</p>
                      )}
                    </div>
                    <div className="mb-4">
                      <p className="text-gray-400 text-xs mb-1">Fee Comprador</p>
                      <p className="text-white font-medium">{tier.buyerFee}%</p>
                    </div>
                    <ul className="space-y-2">
                      {tier.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-xs text-gray-400">
                          <SparklesIcon className="w-3 h-3 text-primary-400 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fee Calculator */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Calculadora de Comisiones</h2>
              <p className="text-gray-400">Calcula cuánto recibirás por tu venta</p>
            </div>

            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Precio de Venta (ETH)</label>
                  <input
                    type="number"
                    value={calculatorPrice}
                    onChange={(e) => setCalculatorPrice(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Tu Tier</label>
                  <select
                    value={calculatorTier}
                    onChange={(e) => setCalculatorTier(e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  >
                    {platformFees.map((tier) => (
                      <option key={tier.tier} value={tier.tier}>
                        {tier.tier} ({tier.sellerFee}%)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-dark-900/50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Precio de Venta</p>
                    <p className="text-xl font-bold text-white">{calculatorPrice} ETH</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Comisión</p>
                    <p className="text-xl font-bold text-red-400">-{feeResult.fee} ETH</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Recibirás</p>
                    <p className="text-xl font-bold text-green-400">{feeResult.net} ETH</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-blue-300 text-sm">
                    El comprador solo paga el precio de venta más el gas de la transacción.
                    No hay comisiones adicionales para compradores.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gas Fees Info */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-8">
              <div className="flex items-center gap-3 mb-6">
                <DocumentTextIcon className="w-8 h-8 text-primary-400" />
                <h2 className="text-2xl font-bold text-white">Sobre el Gas</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">¿Qué es el gas?</h3>
                  <p className="text-gray-400 text-sm">
                    El gas es el costo de ejecutar transacciones en la blockchain.
                    TEMPLUM opera en Polygon, donde el gas es significativamente más
                    bajo que en Ethereum mainnet.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Gas en Polygon</h3>
                  <p className="text-gray-400 text-sm">
                    Las transacciones en Polygon típicamente cuestan entre $0.01 y $0.10 USD,
                    haciendo que las pujas y transferencias sean accesibles para todos.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-dark-700">
                <p className="text-gray-400 text-sm">
                  <span className="text-white font-medium">Nota:</span> Los precios del gas
                  pueden variar según la congestión de la red. Recomendamos verificar el costo
                  estimado en tu wallet antes de confirmar transacciones importantes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Comienza a Stakear para Ahorrar
            </h2>
            <p className="text-gray-400 mb-8">
              Stakea NDH tokens y ahorra hasta un 35% en comisiones de venta
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/staking"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Stakear NDH
              </Link>
              <Link
                to="/ndh-token"
                className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Comprar NDH
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketPricingPage;
